import { useMemo } from "react";
import { Stack, useTheme } from "@mui/material";
import { FoodsIconButton, FoodsTags, MRTGrid, Typography } from "@foodpilot/foods";
import type { MRT_ColumnDef } from "material-react-table";
import { useTranslation } from "react-i18next";
import { SsoProviderListItem } from "../useSsoProviders";
import { getProviderTagLabel, getProviderTagVariant, ProviderTagVariant, readOauthMode } from "../ssoProviderModel";

export type SsoProviderTableRow = {
    item: SsoProviderListItem;
    providerName: string;
    tagLabel: string;
    tagVariant: ProviderTagVariant;
};

type SsoProvidersTableProps = {
    onDelete: (item: SsoProviderListItem) => void;
    onEdit: (item: SsoProviderListItem) => void;
    providers: SsoProviderListItem[];
};

export const SsoProvidersTable = (props: SsoProvidersTableProps) => {
    const { onDelete, onEdit, providers } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const rows = useMemo<SsoProviderTableRow[]>(
        () =>
            providers.map((item) => {
                const mode = readOauthMode(item.protocol, item.raw.protocol);

                return {
                    item,
                    providerName: item.providerName,
                    tagLabel: getProviderTagLabel(item.protocol, mode),
                    tagVariant: getProviderTagVariant(mode),
                };
            }),
        [providers],
    );

    const columns = useMemo<MRT_ColumnDef<SsoProviderTableRow>[]>(
        () => [
            {
                accessorKey: "providerName",
                size: 400,
                header: t("settings.sso.providers.field.provider_name", "Provider name"),
                Cell: ({ row }) => <Typography variant="body-bold">{row.original.providerName}</Typography>,
            },
            {
                accessorKey: "tagLabel",
                size: 280,
                header: t("settings.sso.protocol_placeholder", "Protocol"),
                Cell: ({ row }) => <FoodsTags label={row.original.tagLabel} variant={row.original.tagVariant} />,
            },
        ],
        [t],
    );

    return (
        <MRTGrid
            columns={columns}
            data={rows}
            enableBottomToolbar={false}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enablePagination={false}
            enableColumnPinning
            enableRowActions
            enableRowSelection={false}
            enableTopToolbar={false}
            initialState={{ columnPinning: { right: ["mrt-row-actions"] } }}
            displayColumnDefOptions={{
                "mrt-row-actions": { size: 120, grow: false, header: t("common.actions", "Actions") },
            }}
            renderRowActions={({ row }) => (
                <Stack flexDirection="row" gap={theme.spacing(0.5)}>
                    <FoodsIconButton
                        size="s"
                        icon="edit"
                        tooltip={{ text: t("common.actions.edit", "Edit") }}
                        buttonProps={{
                            "aria-label": t("settings.sso.providers.edit_named", "Edit {{provider}}", {
                                provider: row.original.providerName,
                            }),
                            onClick: () => onEdit(row.original.item),
                        }}
                    />
                    <FoodsIconButton
                        size="s"
                        icon="delete"
                        tooltip={{ text: t("common.actions.delete", "Delete") }}
                        buttonProps={{
                            "aria-label": t("settings.sso.providers.delete_named", "Delete {{provider}}", {
                                provider: row.original.providerName,
                            }),
                            onClick: () => onDelete(row.original.item),
                            sx: { color: theme.palette.error.main },
                        }}
                    />
                </Stack>
            )}
        />
    );
};
